import { devices } from '@src/styles/theme';
import {
  formatBloodProduct,
  formatBloodType,
  formatDeadline,
  formatDeadlineLong,
  formatRelativeTime,
} from '@src/utils/donationFormat';
import Image from 'next/image';
import React from 'react';
import styled, { keyframes } from 'styled-components';

/**
 * 헌혈요청 상세 본문 (Pple Blood Request 디자인 이식).
 *
 * 구성: 뱃지행 → 제목 → 4칸 팩트 그리드 → 본문 → 헌혈 장소 → 금전거래 경고 → 리액션
 * 값이 없는 항목(지역·마감·병원)은 그리지 않는다.
 * 지도는 좌표 데이터가 없어 넣지 않았다(주소 텍스트만 노출).
 */
type Props = {
  community: any;
  onLike?: () => void;
  onShare?: () => void;
  onReport?: () => void;
};

const DonationDetailContent = ({
  community,
  onLike,
  onShare,
  onReport,
}: Props) => {
  const deadline = formatDeadline(community?.deadlineAt);
  const bloodType = formatBloodType(community?.patient?.bloodType);
  const bloodProduct = formatBloodProduct(community?.bloodProduct);
  const views = community?.viewsCount ?? community?.hits ?? 0;
  const images: string[] = community?.images ?? [];

  const facts = [
    { label: '혈액형', value: bloodType, accent: true },
    { label: '헌혈 종류', value: bloodProduct, accent: false },
    {
      label: '필요 수량',
      value: community?.requiredCount ? `${community.requiredCount}팩` : '',
      accent: true,
    },
    {
      label: '마감',
      value: formatDeadlineLong(community?.deadlineAt),
      accent: !!deadline?.urgent,
    },
  ].filter((f) => f.value);

  return (
    <Card>
      <BadgeRow>
        {deadline?.urgent && (
          <UrgentBadge>
            <Dot />
            긴급
          </UrgentBadge>
        )}
        <TagBadge>지정헌혈</TagBadge>
        {community?.region && <RegionBadge>{community.region}</RegionBadge>}
        <MetaText>
          {formatRelativeTime(community?.createdAt)} 등록 · 조회 {views}
        </MetaText>
      </BadgeRow>

      <Title>{community?.title}</Title>

      {facts.length > 0 && (
        <FactGrid count={facts.length}>
          {facts.map((f) => (
            <Fact key={f.label}>
              <FactLabel>{f.label}</FactLabel>
              <FactValue accent={f.accent}>{f.value}</FactValue>
            </Fact>
          ))}
        </FactGrid>
      )}

      <Body>{community?.content}</Body>

      {images.length > 0 && (
        <Images>
          {images.map((src) => (
            <ImageBox key={src}>
              <Image
                src={src}
                width={400}
                height={280}
                loader={() => src}
                alt=""
                style={{ objectFit: 'cover', width: '100%', height: 'auto' }}
              />
            </ImageBox>
          ))}
        </Images>
      )}

      {(community?.hospitalName || community?.hospitalAddress) && (
        <PlaceBox>
          <PlaceHead>
            <PlaceTitle>헌혈 장소</PlaceTitle>
            <PlaceText>
              {[community?.hospitalName, community?.hospitalAddress]
                .filter(Boolean)
                .join(' · ')}
            </PlaceText>
            <MapLink
              href={`https://map.kakao.com/link/search/${encodeURIComponent(
                community?.hospitalAddress || community?.hospitalName
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              지도 앱으로 열기
            </MapLink>
          </PlaceHead>
        </PlaceBox>
      )}

      <Warning>
        <WarnIcon>!</WarnIcon>
        <WarnText>
          헌혈은 어떠한 경우에도 금전 거래의 대상이 될 수 없어요. 사례금·물품을
          요구하거나 제안하는 대화는 즉시 신고해 주세요.
        </WarnText>
      </Warning>

      <Reactions>
        <ReactionButton active={!!community?.liked} onClick={onLike}>
          {community?.liked ? '♥ 응원했어요' : '♡ 응원하기'}{' '}
          {community?.likesCount ?? 0}
        </ReactionButton>
        <ReactionButton active={false} onClick={onShare}>
          공유하기
        </ReactionButton>
        {onReport && <ReportButton onClick={onReport}>신고하기</ReportButton>}
      </Reactions>
    </Card>
  );
};

const Card = styled.article`
  background: #ffffff;
  border: 1px solid #ededed;
  border-radius: 20px;
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 22px;
  @media ${devices.tablet} {
    padding: 20px 16px;
    border-radius: 16px;
    gap: 18px;
  }
`;
const BadgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;
const UrgentBadge = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 12px;
  border-radius: 14px;
  background: #ff4651;
  color: #ffffff;
  font-size: 12.5px;
  font-weight: 700;
`;
const pplePulse = keyframes`
  0%   { opacity: 1;   transform: scale(1); }
  50%  { opacity: 0.35; transform: scale(0.82); }
  100% { opacity: 1;   transform: scale(1); }
`;
const Dot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 3px;
  background: #ffffff;
  animation: ${pplePulse} 1.4s infinite;
`;
const TagBadge = styled.span`
  height: 28px;
  padding: 0 12px;
  border-radius: 14px;
  background: #fff1ed;
  color: #ff4651;
  font-size: 12.5px;
  font-weight: 700;
  display: flex;
  align-items: center;
`;
const RegionBadge = styled.span`
  height: 28px;
  padding: 0 12px;
  border-radius: 14px;
  background: #f4f4f4;
  color: #767676;
  font-size: 12.5px;
  font-weight: 600;
  display: flex;
  align-items: center;
`;
const MetaText = styled.span`
  margin-left: auto;
  font-size: 12.5px;
  color: #b7b7b7;
`;
const Title = styled.h1`
  margin: 0;
  font-family: 'Var', sans-serif;
  font-size: 30px;
  font-weight: 700;
  line-height: 42px;
  letter-spacing: -0.6px;
  color: #222222;
  @media ${devices.tablet} {
    font-size: 22px;
    line-height: 32px;
  }
`;
const FactGrid = styled.div<{ count: number }>`
  display: grid;
  grid-template-columns: repeat(${({ count }) => Math.min(count, 4)}, 1fr);
  gap: 1px;
  background: #ededed;
  border: 1px solid #ededed;
  border-radius: 16px;
  overflow: hidden;
  @media ${devices.tablet} {
    grid-template-columns: repeat(2, 1fr);
  }
`;
const Fact = styled.div`
  background: #ffffff;
  padding: 18px 16px;
  @media ${devices.tablet} {
    padding: 12px;
  }
  display: flex;
  flex-direction: column;
  gap: 7px;
  align-items: center;
`;
const FactLabel = styled.span`
  font-size: 12px;
  color: #9a9a9a;
  @media ${devices.tablet} {
    font-size: 11px;
  }
`;
const FactValue = styled.span<{ accent: boolean }>`
  font-family: 'Var', sans-serif;
  font-size: 19px;
  font-weight: 700;
  color: ${({ accent }) => (accent ? '#FF4651' : '#222222')};
  text-align: center;
  @media ${devices.tablet} {
    font-size: 15px;
  }
`;
const Body = styled.p`
  margin: 0;
  font-size: 15.5px;
  line-height: 28px;
  color: #222222;
  white-space: pre-wrap;
  word-break: break-word;
`;
const Images = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
const ImageBox = styled.div`
  border-radius: 16px;
  overflow: hidden;
  background: #f4f4f4;
`;
const PlaceBox = styled.div`
  border: 1px solid #ededed;
  border-radius: 16px;
  overflow: hidden;
`;
const PlaceHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 18px;
  flex-wrap: wrap;
`;
const PlaceTitle = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #222222;
`;
const PlaceText = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 13px;
  color: #767676;
`;
const MapLink = styled.a`
  flex: none;
  font-size: 13px;
  font-weight: 600;
  color: #ff4651;
  cursor: pointer;
`;
const Warning = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 16px;
  background: #fff1ed;
  border-radius: 14px;
`;
const WarnIcon = styled.div`
  width: 18px;
  height: 18px;
  flex: none;
  margin-top: 1px;
  border-radius: 9px;
  background: #ff4651;
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const WarnText = styled.span`
  font-size: 13px;
  line-height: 21px;
  color: #c0392f;
`;
const Reactions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 6px;
  border-top: 1px solid #f2f2f2;
`;
const ReactionButton = styled.button<{ active: boolean }>`
  height: 38px;
  padding: 0 16px;
  border-radius: 19px;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid ${({ active }) => (active ? '#FF4651' : '#E6E6E6')};
  background: ${({ active }) => (active ? '#FFF1ED' : '#ffffff')};
  color: ${({ active }) => (active ? '#FF4651' : '#767676')};
`;

const ReportButton = styled.button`
  margin-left: auto;
  border: none;
  background: transparent;
  font-size: 13px;
  color: #b7b7b7;
  cursor: pointer;
  &:hover {
    color: #ff4651;
  }
`;

export default DonationDetailContent;
